import { AlertTriangle, History } from "lucide-react";
import { cn } from "@wa/ui";
import { StatusPill } from "../../../ui";
import { GAP_CLASS, type FreshnessFacts, type VersionGap } from "../../../../lib/siteScan";
import type { T } from "../types";
import { Field, Panel } from "./primitives";

/**
 * The comparison the whole feature exists for: what is running, next to what is
 * current.
 *
 * For WordPress the "current" half is a live answer from wordpress.org, down to
 * the vendor's own verdict on the running release ("insecure"), which is the
 * single most quotable line the scanner can produce. Every other platform gets a
 * maintained table, and the badge says which of the two is being shown so nobody
 * quotes a stale number as gospel.
 */

/** A version pair: what is running, what is current, coloured by the gap. */
export function VersionPair({
  current,
  latest,
  gap,
  t,
}: {
  current: string | null;
  latest: string | null;
  gap: VersionGap;
  t: T;
}) {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs">
      <span className={GAP_CLASS[gap]}>{current ?? "?"}</span>
      <span className="text-muted-foreground">/</span>
      <span className="text-muted-foreground">{latest ?? t("scanner.freshness.unknownLatest")}</span>
    </span>
  );
}

export function FreshnessPanel({ freshness, t }: { freshness: FreshnessFacts; t: T }) {
  const platform = freshness.platform;
  const wp = freshness.wp;
  if (!platform && !wp) return null;

  const core = wp?.core;
  const insecure = core?.status === "insecure";

  return (
    <Panel
      icon={History}
      title={t("scanner.freshness.title")}
      aside={
        platform ? (
          <StatusPill tone="mut">{t(`scanner.freshness.source.${platform.source}`)}</StatusPill>
        ) : undefined
      }
    >
      <div className="space-y-3">
        {platform && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="text-sm font-medium">{platform.name}</span>
            <VersionPair current={platform.current} latest={platform.latest} gap={platform.gap} t={t} />
            {platform.gap !== "unknown" && platform.gap !== "current" && (
              // The gap keeps the scanner's own colour (`GAP_CLASS`): major,
              // minor and patch are three steps of one measurement, which is
              // more than the CRM's five tones are meant to carry.
              <StatusPill tone="mut" className={cn("font-mono", GAP_CLASS[platform.gap])}>
                {t(`scanner.gap.${platform.gap}`)}
              </StatusPill>
            )}
            {core?.behind != null && core.behind > 0 && (
              <span className="text-xs text-muted-foreground">
                {t("scanner.freshness.releasesBehind", { count: core.behind })}
              </span>
            )}
            {insecure && (
              <StatusPill tone="lt">
                <AlertTriangle className="size-3" /> {t("scanner.freshness.insecure")}
              </StatusPill>
            )}
          </div>
        )}

        {wp && (
          <dl className="grid grid-cols-2 gap-3 border-t border-[var(--crm-hair)] pt-3 sm:grid-cols-4">
            <Field label={t("scanner.freshness.plugins")} value={String(wp.plugins.length)} />
            <Field
              label={t("scanner.freshness.outdated")}
              value={
                <span className="crm-tnum" style={wp.outdatedPlugins > 0 ? { color: "var(--color-warning)" } : undefined}>
                  {wp.outdatedPlugins}
                </span>
              }
            />
            <Field
              label={t("scanner.freshness.abandoned")}
              value={
                <span className="crm-tnum" style={wp.abandonedPlugins > 0 ? { color: "var(--color-destructive)" } : undefined}>
                  {wp.abandonedPlugins}
                </span>
              }
            />
            <Field label={t("scanner.freshness.unlisted")} value={String(wp.unlistedPlugins)} />
          </dl>
        )}

        {wp?.theme && (
          <div className="flex flex-wrap items-center gap-2 border-t border-[var(--crm-hair)] pt-3">
            <span className="text-[11px] font-medium text-muted-foreground">{t("scanner.wp.theme")}</span>
            <span className="font-mono text-xs">{wp.theme.name}</span>
            <VersionPair current={wp.theme.current} latest={wp.theme.latest} gap={wp.theme.gap} t={t} />
            {wp.theme.unlisted && <StatusPill tone="mut">{t("scanner.freshness.premium")}</StatusPill>}
          </div>
        )}
      </div>
    </Panel>
  );
}
